import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Server, Link as LinkIcon, BarChart, Tag, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientProperties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API}/api/client/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProperties(data.digital_properties || {});
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading properties...</p>
        </div>
      </div>
    );
  }

  const PropertyCard = ({ icon, title, value, link }) => (
    <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/20 hover:border-white/40 transition-all">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-blue-200 text-xs mb-1">{title}</p>
          {value ? (
            link ? (
              <a
                href={value.startsWith('http') ? value : `https://${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-medium text-sm hover:text-yellow-300 transition truncate block"
              >
                {value}
              </a>
            ) : (
              <p className="text-white font-medium text-sm break-words">{value}</p>
            )
          ) : (
            <p className="text-gray-400 text-sm italic">Not configured</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-black/20 backdrop-blur-lg">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client-dashboard')}
              className="text-white hover:bg-white/10 touch-manipulation"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Digital Properties</h1>
              <p className="text-blue-200 text-sm">Your website and marketing accounts</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-6 bg-blue-400/10 backdrop-blur-xl rounded-2xl p-4 border border-blue-400/30">
          <p className="text-blue-200 text-sm flex items-start gap-2">
            <Globe className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Your Project Manager manages these properties. Contact them if you need any changes.</span>
          </p>
        </div>

        {/* Website Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-yellow-400" />
            Website
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PropertyCard
              icon={<Globe className="h-5 w-5 text-white" />}
              title="Primary Website URL"
              value={properties.primary_website_url}
              link={true}
            />
            <PropertyCard
              icon={<Tag className="h-5 w-5 text-white" />}
              title="CMS Platform"
              value={properties.cms_platform}
            />
            <PropertyCard
              icon={<Server className="h-5 w-5 text-white" />}
              title="Hosting Provider"
              value={properties.hosting_provider}
            />
            <PropertyCard
              icon={<LinkIcon className="h-5 w-5 text-white" />}
              title="Domain Registrar"
              value={properties.domain_registrar}
            />
          </div>
        </div>

        {/* Marketing Accounts Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <BarChart className="h-5 w-5 text-yellow-400" />
            Marketing & Analytics
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PropertyCard
              icon={<BarChart className="h-5 w-5 text-white" />}
              title="Google Ads Account ID"
              value={properties.google_ads_account_id}
            />
            <PropertyCard
              icon={<BarChart className="h-5 w-5 text-white" />}
              title="Meta Business Manager ID"
              value={properties.meta_business_manager_id}
            />
            <PropertyCard
              icon={<BarChart className="h-5 w-5 text-white" />}
              title="Google Analytics Property ID"
              value={properties.google_analytics_property_id}
            />
            <PropertyCard
              icon={<Tag className="h-5 w-5 text-white" />}
              title="Google Tag Manager Container ID"
              value={properties.google_tag_manager_container_id}
            />
          </div>
        </div>

        {/* Access Information Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Lock className="h-5 w-5 text-yellow-400" />
            Access Information
          </h2>
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-5 border border-white/20">
            <div className="space-y-4">
              {properties.login_url && (
                <div>
                  <p className="text-blue-200 text-xs mb-2">Login URL</p>
                  <a
                    href={properties.login_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white font-medium hover:text-yellow-300 transition block break-all"
                  >
                    {properties.login_url}
                  </a>
                </div>
              )}
              
              {properties.login_username && (
                <div>
                  <p className="text-blue-200 text-xs mb-2">Username</p>
                  <p className="text-white font-medium">{properties.login_username}</p>
                </div>
              )}
              
              {properties.login_notes && (
                <div>
                  <p className="text-blue-200 text-xs mb-2">Notes</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{properties.login_notes}</p>
                </div>
              )}
              
              {!properties.login_url && !properties.login_username && !properties.login_notes && (
                <p className="text-gray-400 text-sm italic">No access information provided yet</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Animation styles */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
